import React from 'react'
import { useField, type FieldProps } from 'remix-validated-form'
import { AppConfig } from '~/AppConfig'

export type WithUseFieldProps = {
  name: string
  children: (props: FieldProps) => React.ReactNode
}

/**
 * A wrapper component that utilizes the `useField` hook from `remix-validated-form`
 * to manage form field state and validation. This component allows you to access
 * field-specific properties and methods via a render prop pattern.
 *
 * @param {string} props.name - The name of the field to manage.
 *
 * @returns {React.ReactNode} The rendered child component with field props.
 *
 * @example
 * ```tsx
 * <WithUseField name="username">
 *   {({ error, getInputProps }) => (
 *     <div>
 *       <input {...getInputProps()} placeholder="Enter your username" />
 *       {error && <span style={{ color: 'red' }}>{error}</span>}
 *     </div>
 *   )}
 * </WithUseField>
 * ```
 */
export function WithUseField(props: WithUseFieldProps) {
  const fieldProps = useField(props.name, {
    validationBehavior: AppConfig.InputValidationBehavior
  })

  return <>{props.children(fieldProps)}</>
}
